import SoftwareTrackerApiClient from '~/apiClients/SoftwareTrackerApiClient';
import { Tracker, TrackerAttributes, TrackerOperationAttributes } from './Tracker';
import { TrackerSetting, TrackerSettingAttributes, TrackerSettingOperationAttributes } from './TrackerSetting';
import DateHelper from '~/helpers/DateHelper';

export interface SoftwareTrackerAttributes {
  id: number,
  trackerId: number,
  trackerSettingId: number,
  createdAt: string,
  updatedAt?: string,
  tracker: TrackerAttributes,
  trackerSetting: TrackerSettingAttributes,
}

export interface SoftwareTrackerCreationAttributes {
  TrackerSetting: TrackerSettingOperationAttributes,
  Tracker: TrackerOperationAttributes,
}

export interface SoftwareTrackerUpdateAttributes {
  id: number,
  trackerSetting?: {
    clientId?: string,
    isLive?: boolean,
    defaultFieldValueType?: string,
    defaultFieldValueText?: string,
    extraFields?: Record<string, string> | null,
  },
  tracker?: {
    name: string,
    version: string,
    autoSync: number,
  },
}

export class SoftwareTracker {
  constructor(private readonly attributes: SoftwareTrackerAttributes) {
  }

  get id(): number {
    return this.attributes.id;
  }

  get trackerId(): number {
    return this.attributes.trackerId;
  }

  get trackerSettingId(): number {
    return this.attributes.trackerSettingId;
  }

  get lastModified(): string {
    const trackerDate = this.tracker.updatedAt ?? this.tracker.createdAt;
    const trackerSettingDate = this.trackerSetting.updatedAt ?? this.trackerSetting.createdAt;

    return new Date(trackerDate) > new Date(trackerSettingDate) ? DateHelper.format(trackerDate) : DateHelper.format(trackerSettingDate);
  }

  get tracker(): Tracker {
    return new Tracker(this.attributes.tracker);
  }

  get trackerSetting(): TrackerSetting {
    return new TrackerSetting(this.attributes.trackerSetting);
  }

  static async findByTrackerId(trackerId: number) {
    return SoftwareTracker.getApiClient().provideSoftwareTracker(trackerId);
  }

  static async create(formData: { softwareId: number, name: string, version: string, type: number, clientId?: string, defaultFieldValueType: string, defaultFieldValueText: string, extraFields?: Record<string, string> }) {
    const { softwareId, clientId, defaultFieldValueType, defaultFieldValueText, extraFields, name, type, version } = formData;
    const apiClient = SoftwareTracker.getApiClient();

    return apiClient.createSoftwareTracker({
      TrackerSetting: {
        clientId,
        defaultFieldValueType,
        defaultFieldValueText,
        extraFields,
      },
      Tracker: {
        softwareId,
        name,
        type,
        version,
      },
    });
  }

  public async clone(): Promise<SoftwareTracker> {
    return await SoftwareTracker.getApiClient().cloneSoftwareTracker(this.attributes.id);
  }

  public async changeLiveStatus() {
    const attributes: SoftwareTrackerUpdateAttributes = {
      id: this.attributes.id,
      trackerSetting: {
        isLive: !this.attributes.trackerSetting.isLive,
      },
    }
    return await SoftwareTracker.getApiClient().updateSoftwareTracker(attributes);
  }

  public async update(name: string, version: string, clientId: string = '', defaultFieldValueType: string = '', defaultFieldValueText: string = '', defaultAutoSync: number = 0, extraFields: Record<string, string> = {}) {
    const updatedAttributes: SoftwareTrackerUpdateAttributes = {
      id: this.attributes.id,
      trackerSetting: {
        clientId,
        defaultFieldValueType,
        defaultFieldValueText,
        extraFields,
      },
      tracker: {
        name,
        version,
        autoSync: defaultAutoSync,
      }
    };
    return await SoftwareTracker.getApiClient().updateSoftwareTracker(updatedAttributes);
  }

  private static getApiClient(): SoftwareTrackerApiClient {
    return new SoftwareTrackerApiClient();
  }
}
